import React, { useEffect, useState, useRef } from "react";
import styles from "./styles.module.scss";
import theme from "../../Styles/theme.scss";
import ToggleOnOff from "../ToggleOnOff";
import {
  getDevelcoSensorLEDStatus,
  postDevelcoSensorLEDStatus,
} from "@intelligentlilli/api-layer";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";
import { getLEDCacheKey, LEDCache, setCachedLEDState } from "../../Services/Constants";

interface LEDSensorToggleProps {
  server: string;
  devicestring: string;
  sensorIds: string[];
}

const LED_CACHE_TTL_MS = 900_000; // 15 minutes

const getCachedLEDState = (devicestring: string): boolean | null => {
  const key = getLEDCacheKey(devicestring);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const cache: LEDCache = JSON.parse(raw);
    if (Date.now() - cache.timestamp < LED_CACHE_TTL_MS) {
      return cache.state;
    }
  } catch {
    localStorage.removeItem(key);
  }
  return null;
};

const LEDSensorToggle = ({
  server,
  devicestring,
  sensorIds,
}: LEDSensorToggleProps) => {
  const track = useAmplitudeTracking();
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isTogglingRef = useRef<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const sensorIdsKey = sensorIds.join(",");

  // Fetch current LED status from all motion & door sensors on mount
  // If a recent POST was made (within 15 min), trust the cached state instead
  useEffect(() => {
    if (sensorIds.length === 0) return;

    const cachedState = getCachedLEDState(devicestring);
    if (cachedState !== null) {
      setIsEnabled(cachedState);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const promises = sensorIds.map((sensorId) => {
      return getDevelcoSensorLEDStatus(server, devicestring, sensorId, "web")
    });

    Promise.allSettled(promises).then((results) => {
      if (cancelled) return;

      const allOn = results.every(
        (result) =>
          result.status === "fulfilled" &&
          result.value.ok &&
          result.value.data &&
          "data" in result.value.data &&
          result.value.data.data === true
      );
      
      setIsEnabled(allOn);
      setIsLoading(false);
      track("Device Monitoring", {
        action: `sensor_LED_is_on_${allOn}`,
        isSuccess: "true",
      });
    }).catch(() => {
      setError("Could not get LED status. Refresh to try again");
      setIsLoading(false);
      track("Device Monitoring", {
        action: "sensor_LED_fetch_failed",
        isSuccess: "false",
      });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server, devicestring, sensorIdsKey]);

  const toggleLEDs = (newStatus: boolean) => {
    if (isTogglingRef.current) return;
    isTogglingRef.current = true;
    setIsLoading(true);
    setError(null);

    const promises = sensorIds.map((sensorId) =>
      postDevelcoSensorLEDStatus(
        server,
        devicestring,
        sensorId,
        { state: newStatus },
        "web"
      )
    );

    Promise.allSettled(promises).then((results) => {
      const allSucceeded = results.every(
        (result) =>
          result.status === "fulfilled" && result.value.ok
      );

      if (allSucceeded) {
        setIsEnabled(newStatus);
        setCachedLEDState(devicestring, newStatus);
      } else {
        setError("Could not update. Try again");
      }

      isTogglingRef.current = false;
      setIsLoading(false);
    }).catch(() => {
      setError("Could not update. Try again");
      isTogglingRef.current = false;
      setIsLoading(false);
    });
  };

  if (sensorIds.length === 0) return null;

  return (
    <div className={styles.container}>
      <p className={styles.title}>Motion and door sensor LEDs</p>
      <div className={styles.bottom_row}>
        <p className={styles.description}>
          Toggle LEDs on or off. Be mindful that with the LEDs off, it will
          be harder to tell if the motion sensors are working.
        </p>
        <div className={styles.toggle_wrapper}>
          <ToggleOnOff
            isEnabled={isEnabled}
            onToggleOn={() => toggleLEDs(true)}
            onToggleOff={() => toggleLEDs(false)}
            backgroundColor={theme.primary3}
            loading={isLoading}
            enabledLabel="On"
            disabledLabel="Off"
          />
        </div>
      </div>
      <p className={styles.error}>{error}</p>
    </div>
  );
};

export default LEDSensorToggle;
